import { useCallback, useEffect, useRef, useState } from 'react';

import Quill from 'quill';

export type QuillClipboardMatcher = (node: HTMLElement, delta: any) => any;

export type UseQuillEditorConfig = {
  value: string;
  onChange: (serialized: string) => void;
  serialize: (editor: Quill) => string;
  /** Return true when seeding normalized the content and the parent should be notified. */
  seedEditor: (editor: Quill, value: string) => boolean | void;
  formats: string[];
  /** Extra Quill modules, merged over `{ toolbar: false }`. */
  modules?: Record<string, unknown>;
  readOnly?: boolean;
  matchers?: ReadonlyArray<readonly [string, QuillClipboardMatcher]>;
  /** Runs before the default text-change pipeline; return true to skip it. */
  onTextChange?: (editor: Quill, ops: any[], source: string) => boolean;
  isEmpty?: (editor: Quill) => boolean;
  configureEditor?: (editor: Quill) => void;
  autoFocus?: boolean;
  /** Rebuild the editor instance when these change (after first render). */
  remountDeps?: unknown[];
};

const defaultIsEmpty = (editor: Quill): boolean =>
  editor.getText().trim().length === 0;

// Shared Quill lifecycle for the composers. Everything the instance touches
// is read through a ref, so changing prop identities never recreate it.
export const useQuillEditor = (config: UseQuillEditorConfig) => {
  const { value, remountDeps = [] } = config;

  const editorRef = useRef<HTMLDivElement | null>(null);
  const [quill, setQuill] = useState<Quill | null>(null);
  const [isBlank, setIsBlank] = useState(true);
  const currentValueRef = useRef(value);
  const didAutoFocusRef = useRef(false);

  const configRef = useRef(config);
  configRef.current = config;

  const [editorKey, setEditorKey] = useState(0);
  const isFirstRenderRef = useRef(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- remountDeps is the caller's deps list
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setEditorKey(key => key + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, remountDeps);

  const seed = useCallback((editor: Quill, nextValue: string) => {
    const cfg = configRef.current;
    const normalized = cfg.seedEditor(editor, nextValue);
    const serialized = cfg.serialize(editor);
    currentValueRef.current = serialized;
    setIsBlank((cfg.isEmpty ?? defaultIsEmpty)(editor));
    if (normalized === true) {
      cfg.onChange(serialized);
    }
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (el == null) {
      return;
    }
    const cfg = configRef.current;
    const editor = new Quill(el, {
      readOnly: cfg.readOnly ?? false,
      formats: cfg.formats,
      modules: { toolbar: false, ...cfg.modules },
    });
    for (const [selector, matcher] of cfg.matchers ?? []) {
      editor.clipboard.addMatcher(selector, matcher as any);
    }
    cfg.configureEditor?.(editor);
    seed(editor, cfg.value);

    editor.on('text-change', (delta, _old, source) => {
      const c = configRef.current;
      if (c.onTextChange?.(editor, delta.ops ?? [], source) === true) {
        return;
      }
      setIsBlank((c.isEmpty ?? defaultIsEmpty)(editor));
      if (source !== 'user') {
        return;
      }
      const serialized = c.serialize(editor);
      currentValueRef.current = serialized;
      c.onChange(serialized);
    });

    setQuill(editor);

    if (
      cfg.autoFocus === true &&
      cfg.readOnly !== true &&
      !didAutoFocusRef.current
    ) {
      didAutoFocusRef.current = true;
      requestAnimationFrame(() => editor.focus());
    }

    return () => {
      editor.off('text-change');
      setQuill(null);
      el.innerHTML = '';
    };
  }, [editorKey, seed]);

  useEffect(() => {
    if (quill == null) {
      return;
    }
    if (value !== currentValueRef.current) {
      seed(quill, value);
    }
  }, [quill, value, seed]);

  return { editorRef, quill, isBlank, editorKey };
};
